/**
 * Centralized Axios instance with authentication interceptors
 * Handles token expiration and automatic logout on 401 errors
 * 
 * This module sets up interceptors on both:
 * 1. A custom axiosInstance (for explicit use)
 * 2. The default axios instance (to catch all axios calls)
 */
import axios from 'axios';
import API_BASE from '../config';

// Helper function to handle 401 errors
let handling401 = false;
const handle401Error = (error) => {
  // Anonymous session: there is nothing to clear, and reloading on a stray
  // 401 from an optional-auth call would loop the page.
  if (!localStorage.getItem('token')) return;
  // A page load fires many requests in parallel; if several 401 together,
  // reload once — not once per failed request.
  if (handling401) return;
  handling401 = true;

  const errorMessage = error.response?.data?.error || error.response?.data?.message;
  
  // Check if it's a token expiration error
  const isExpired = errorMessage === 'Token expired' || 
                    errorMessage === 'Token has expired' ||
                    errorMessage?.toLowerCase().includes('expired');
  
  if (isExpired) {
    console.log('Token expired, clearing authentication and reloading...');
  } else {
    console.log('Authentication failed, clearing auth data...');
  }
  
  // Clear all auth-related data
  localStorage.removeItem('token');
  localStorage.removeItem('userId');
  localStorage.removeItem('userName');
  localStorage.removeItem('userPic');
  
  // Dispatch custom event to notify components
  window.dispatchEvent(new CustomEvent('auth-expired', {
    detail: { 
      message: isExpired 
        ? 'Your session has expired. Please sign in again.' 
        : 'Authentication failed. Please sign in again.' 
    }
  }));
  
  // Reload the page to reset auth state
  // Small delay to allow event to propagate
  setTimeout(() => {
    window.location.reload();
  }, 100);
};

// Request interceptor: Add auth token to all requests
const requestInterceptor = (config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
};

// Response interceptor: Handle 401 errors globally
const responseInterceptor = {
  onFulfilled: (response) => response,
  onRejected: (error) => {
    // Handle 401 Unauthorized errors (token expired or invalid)
    if (error.response?.status === 401) {
      handle401Error(error);
    }
    return Promise.reject(error);
  }
};

// Create axios instance with baseURL
const axiosInstance = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Set up interceptors on the custom instance
axiosInstance.interceptors.request.use(requestInterceptor);
axiosInstance.interceptors.response.use(
  responseInterceptor.onFulfilled,
  responseInterceptor.onRejected
);

// Also set up interceptors on the default axios instance
// This ensures ALL axios calls (even direct imports) are covered
axios.interceptors.request.use(requestInterceptor);
axios.interceptors.response.use(
  responseInterceptor.onFulfilled,
  responseInterceptor.onRejected
);

export default axiosInstance;

